import { FC, useMemo } from 'react';
import { clsx } from 'clsx';
import { MdHeadsetMic, MdInfoOutline, MdOpenInNew } from 'react-icons/md';
import Preloader from '../Preloader';
import PageHero from '../PageHero';
import './SupportContent.scss';

interface ISupportLink {
    href: string;
    host: string;
    target: string | undefined;
    text: string;
}

export interface ISupportContentProps {
    html: string;
    isCompact?: boolean;
    loading?: boolean;
}

const normalizeText = (value: string) => {
    return value.replace(/\s+/g, ' ').trim();
};

const getSupportLinks = (html: string): ISupportLink[] => {
    if (!html) {
        return [];
    }

    const parser = new DOMParser();
    const document = parser.parseFromString(html, 'text/html');

    return Array.from(document.querySelectorAll('a'))
        .map((anchor) => {
            const href = anchor.getAttribute('href')?.trim() ?? '';
            const text = normalizeText(anchor.textContent ?? '');

            if (!href || !text) {
                return null;
            }

            let host: string;

            try {
                host = new URL(href, window.location.origin).hostname.replace(/^www\./, '');
            } catch {
                host = '';
            }

            return {
                href,
                host,
                target: anchor.getAttribute('target')?.trim() ?? undefined,
                text,
            };
        })
        .filter((item): item is ISupportLink => item !== null);
};

const SupportContent: FC<ISupportContentProps> = ({ html, isCompact = false, loading = false }) => {
    const links = useMemo(() => getSupportLinks(html), [html]);

    return (
        <div className={clsx('rf-support-content', isCompact && 'rf-support-content--compact')}>
            <div className="rf-support-content__header">
                <PageHero
                    icon={<MdHeadsetMic />}
                    title="Техническая поддержка"
                    subtitle="Быстрые ссылки"
                    headingLevel={2}
                />
                <div className="rf-support-content__description">
                    Откройте обращение в поддержку или перейдите в базу знаний.
                </div>
            </div>

            {loading ? (
                <div className="rf-support-content__state">
                    <Preloader size="lg" position="static" />
                </div>
            ) : links.length > 0 ? (
                <div className="rf-support-content__list">
                    {links.map((link) => (
                        <a
                            key={link.href}
                            className="rf-support-content__card"
                            href={link.href}
                            target={link.target}
                            rel={link.target === '_blank' ? 'noreferrer' : undefined}
                        >
                            <div className="rf-support-content__card-accent" />
                            <div className="rf-support-content__card-body">
                                <div className="rf-support-content__card-title">{link.text}</div>
                                {link.host && <div className="rf-support-content__card-meta">{link.host}</div>}
                            </div>
                            <MdOpenInNew className="rf-support-content__card-icon" size="20" />
                        </a>
                    ))}
                </div>
            ) : (
                <div className="rf-support-content__empty">
                    <MdInfoOutline size="20" />
                    <span>Ссылки поддержки сейчас недоступны.</span>
                </div>
            )}
        </div>
    );
};

export default SupportContent;
